import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ChineseNumerologyCalculator = () => {
  const [birthYear, setBirthYear] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  // Chinese Year Meanings
  const chineseYears = {
    0: {
      animal: 'Monkey',
      element: 'Metal',
      power: 'Wisdom Power',
      traits: [
        'Intelligent',
        'Witty',
        'Creative',
        'Adaptable',
        'Innovative'
      ],
      description: 'People born in the Year of the Metal Monkey are intelligent, witty, and creative. They have a natural talent for problem-solving and innovation.',
      years: '1920, 1980, 2040'
    },
    1: {
      animal: 'Rooster',
      element: 'Metal',
      power: 'Precision Power',
      traits: [
        'Organized',
        'Detail-oriented',
        'Practical',
        'Efficient',
        'Resourceful'
      ],
      description: 'People born in the Year of the Metal Rooster are organized and detail-oriented. They have a strong sense of practicality and efficiency.',
      years: '1921, 1981, 2041'
    },
    2: {
      animal: 'Dog',
      element: 'Earth',
      power: 'Loyalty Power',
      traits: [
        'Loyal',
        'Trustworthy',
        'Honest',
        'Dependable',
        'Kind'
      ],
      description: 'People born in the Year of the Earth Dog are loyal and trustworthy. They have strong moral principles and are known for their kindness.',
      years: '1934, 1994, 2054'
    },
    3: {
      animal: 'Pig',
      element: 'Earth',
      power: 'Prosperity Power',
      traits: [
        'Lucky',
        'Prosperous',
        'Generous',
        'Optimistic',
        'Social'
      ],
      description: 'People born in the Year of the Earth Pig are lucky and prosperous. They have a generous nature and are optimistic about life.',
      years: '1935, 1995, 2055'
    },
    4: {
      animal: 'Rat',
      element: 'Water',
      power: 'Resourcefulness Power',
      traits: [
        'Resourceful',
        'Adaptable',
        'Quick-witted',
        'Intelligent',
        'Ambitious'
      ],
      description: 'People born in the Year of the Water Rat are resourceful and adaptable. They have quick wits and are ambitious in their pursuits.',
      years: '1936, 1996, 2056'
    },
    5: {
      animal: 'Ox',
      element: 'Water',
      power: 'Steadfast Power',
      traits: [
        'Steadfast',
        'Determined',
        'Reliable',
        'Patient',
        'Practical'
      ],
      description: 'People born in the Year of the Water Ox are steadfast and determined. They are reliable and have strong practical skills.',
      years: '1937, 1997, 2057'
    },
    6: {
      animal: 'Tiger',
      element: 'Wood',
      power: 'Courage Power',
      traits: [
        'Courageous',
        'Confident',
        'Leadership',
        'Competitive',
        'Charismatic'
      ],
      description: 'People born in the Year of the Wood Tiger are courageous and confident. They have strong leadership qualities and are naturally competitive.',
      years: '1938, 1998, 2058'
    },
    7: {
      animal: 'Rabbit',
      element: 'Wood',
      power: 'Grace Power',
      traits: [
        'Graceful',
        'Sensitive',
        'Kind',
        'Artistic',
        'Social'
      ],
      description: 'People born in the Year of the Wood Rabbit are graceful and sensitive. They have a kind nature and are often artistic and social.',
      years: '1939, 1999, 2059'
    },
    8: {
      animal: 'Dragon',
      element: 'Earth',
      power: 'Majestic Power',
      traits: [
        'Majestic',
        'Powerful',
        'Confident',
        'Innovative',
        'Ambitious'
      ],
      description: 'People born in the Year of the Earth Dragon are majestic and powerful. They have strong confidence and are innovative in their approach.',
      years: '1940, 2000, 2060'
    },
    9: {
      animal: 'Snake',
      element: 'Earth',
      power: 'Wisdom Power',
      traits: [
        'Wise',
        'Intuitive',
        'Mysterious',
        'Resourceful',
        'Strategic'
      ],
      description: 'People born in the Year of the Earth Snake are wise and intuitive. They have a mysterious nature and are resourceful in their strategies.',
      years: '1941, 2001, 2061'
    },
    10: {
      animal: 'Horse',
      element: 'Fire',
      power: 'Freedom Power',
      traits: [
        'Free-spirited',
        'Adventurous',
        'Energetic',
        'Independent',
        'Ambitious'
      ],
      description: 'People born in the Year of the Fire Horse are free-spirited and adventurous. They have boundless energy and are independent in their pursuits.',
      years: '1942, 2002, 2062'
    },
    11: {
      animal: 'Sheep',
      element: 'Fire',
      power: 'Harmony Power',
      traits: [
        'Harmonious',
        'Peaceful',
        'Creative',
        'Sensitive',
        'Artistic'
      ],
      description: 'People born in the Year of the Fire Sheep are harmonious and peaceful. They have strong creative abilities and are sensitive to their surroundings.',
      years: '1943, 2003, 2063'
    }
  };

  const handleYearChange = (e) => {
    setBirthYear(e.target.value);
    setError('');
  };

  const calculateChinesePower = () => {
    if (!birthYear) {
      setError('Please enter your birth year');
      return;
    }

    const year = parseInt(birthYear);
    if (isNaN(year) || year < 1900 || year > 2100) {
      setError('Please enter a valid birth year between 1900 and 2100');
      return;
    }

    const chineseYearIndex = (year - 1900) % 12;
    const chineseYearData = chineseYears[chineseYearIndex];

    setResults({
      year,
      animal: chineseYearData.animal,
      element: chineseYearData.element,
      power: chineseYearData.power,
      traits: chineseYearData.traits,
      description: chineseYearData.description,
      years: chineseYearData.years
    });

    // Prepare chart data
    const chineseChartData = {
      labels: ['Power', 'Traits'],
      datasets: [{
        label: 'Chinese Power Distribution',
        data: [70, 30],
        backgroundColor: [
          'rgba(153, 102, 255, 0.7)',
          'rgba(255, 159, 64, 0.7)'
        ],
        borderColor: [
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)'
        ],
        borderWidth: 1,
        hoverOffset: 4
      }]
    };

    setChartData(chineseChartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Chinese Numerology Calculator | Calculate Your Chinese Year Power</title>
        <meta name="description" content="Calculate the numerological significance of your Chinese birth year using our Chinese Numerology Calculator. Understand your personality traits and potential based on Chinese astrology." />
        <meta name="keywords" content="chinese numerology, chinese astrology calculator, personality traits, chinese zodiac analysis, birth year calculator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourwebsite.com/calculator/chinese-numerology" />
        <meta property="og:title" content="Chinese Numerology Calculator | Calculate Your Chinese Year Power" />
        <meta property="og:description" content="Calculate the numerological significance of your Chinese birth year using our Chinese Numerology Calculator. Understand your personality traits and potential based on Chinese astrology." />
        <meta property="og:image" content="https://yourwebsite.com/images/chinese-numerology-calculator.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourwebsite.com/calculator/chinese-numerology" />
        <meta property="twitter:title" content="Chinese Numerology Calculator | Calculate Your Chinese Year Power" />
        <meta property="twitter:description" content="Calculate the numerological significance of your Chinese birth year using our Chinese Numerology Calculator. Understand your personality traits and potential based on Chinese astrology." />
        <meta property="twitter:image" content="https://yourwebsite.com/images/chinese-numerology-calculator.png" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🐉</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Chinese Numerology Calculator</h1>
            <p className="text-lg text-purple-600 font-semibold">Calculate Your Chinese Year Power</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="year">
            Your Birth Year:
          </label>
          <input
            type="number"
            id="year"
            name="year"
            value={birthYear}
            onChange={handleYearChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter your birth year (e.g., 1990)"
          />
        </div>

        <button
          onClick={calculateChinesePower}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Chinese Year Power
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Chinese Year Power</h3>
            
            <div className="space-y-12">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="space-y-8">
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Chinese Year:</span>
                      <span className="font-medium text-purple-600 text-2xl">{results.year}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Animal Sign:</h5>
                    <p className="text-purple-600 text-xl">{results.animal}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Element:</h5>
                    <p className="text-purple-600 text-xl">{results.element}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Power Type:</h5>
                    <p className="text-purple-600 text-xl">{results.power}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Key Traits:</h5>
                    <ul className="list-disc pl-6 space-y-2">
                      {results.traits.map((trait, index) => (
                        <li key={index} className="text-gray-700">{trait}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Description:</h5>
                    <p className="text-gray-700">{results.description}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Other Years:</h5>
                    <p className="text-gray-700">{results.years}</p>
                  </div>
                </div>
              </div>

              {/* Power Visualization Section */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Power Visualization</h4>
                <div className="h-96 bg-gray-50 rounded-xl p-6">
                  <Doughnut
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Chinese Power Distribution',
                          color: '#4F46E5',
                          font: {
                            size: 16
                          }
                        },
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: {
                            color: '#4B5563',
                            font: {
                              size: 12
                            }
                          }
                        }
                      },
                      cutout: '60%',
                      animation: {
                        animateRotate: true,
                        animateScale: true
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Examples & Interpretations <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 1: Year of the Metal Monkey</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Birth Year: 1980</p>
                <p className="text-purple-600 text-2xl mb-2">Animal: Monkey</p>
                <p className="text-purple-600 text-2xl mb-2">Element: Metal</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This birth year indicates strong wisdom power, intelligence, and creativity. People born in this year are naturally innovative and adaptable.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 2: Year of the Earth Dog</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Birth Year: 1994</p>
                <p className="text-purple-600 text-2xl mb-2">Animal: Dog</p>
                <p className="text-purple-600 text-2xl mb-2">Element: Earth</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This birth year suggests strong loyalty power, trustworthiness, and kindness. People born in this year are known for their moral principles and dependability.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your Chinese numerology:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Your Birth Year</h3>
              <p className="text-gray-700">
                Enter your birth year (e.g., 1990) in the input field.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate Chinese Year Power" button to see your results.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review your Chinese year's animal sign, element, power type, key traits, and description to understand your personality potential.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What is Chinese Numerology?</h3>
              <p className="text-gray-700">
                Chinese Numerology combines Chinese astrology and numerology to analyze the significance of your birth year based on the Chinese zodiac system. It considers both the animal sign and the elemental influence of your birth year.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How is it calculated?</h3>
              <p className="text-gray-700">
                The calculator determines your Chinese zodiac animal and element based on your birth year and matches it with its corresponding numerological meanings, traits, and descriptions.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What does it reveal?</h3>
              <p className="text-gray-700">
                It shows your Chinese zodiac animal, element, power type, key traits, strengths, challenges, and potential based on Chinese numerological principles.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Chinese zodiac animal identification</li>
                <li>Elemental influence analysis</li>
                <li>Power type assessment</li>
                <li>Personality trait evaluation</li>
                <li>Comprehensive descriptions</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Understand personality traits</li>
                <li>Make informed decisions</li>
                <li>Enhance self-awareness</li>
                <li>Support personal growth</li>
                <li>Improve relationships</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChineseNumerologyCalculator;
